/**
 * Medication Administration Logs Component
 * 
 * Displays logs for a specific medication administration form
 * Features:
 * - Shows daily medication administration logs
 * - Staff signatures and comments
 * - Dosage tracking
 * - Time tracking
 * - Read-only view for parents
 */

import React, { useState, useEffect } from 'react'
import { Row, Col, Container, Badge, Table } from 'react-bootstrap'
import Card from '../../../../components/Card'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { 
  fetchMedicationLogs, 
  selectMedicationLogs, 
  selectLogsLoading, 
  selectLogsError,
  clearErrors 
} from '../../../../store/medication/medicationSlice';

import LoaderModal from '../../../../components/loaderModal';
import NoDataFound from '../../../../components/noDataFound';
import { formatDisplayDate } from '../../../../utilities/displayFormat';

//Sweet alert
import Swal from 'sweetalert2'

const MedicationLogs = ({ type = 1 }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  
  // Redux state
  const logs = useSelector(selectMedicationLogs);
  const loading = useSelector(selectLogsLoading);
  const error = useSelector(selectLogsError);

  const [isFirst, setIsFirst] = useState(true);
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setIsFirst(false)
  }, [isFirst]);

  // Load medication logs on component mount
  useEffect(() => {
    if (localStorage.getItem("uzoneAuthToken") && id) {
      dispatch(fetchMedicationLogs({ administrationId: id, type }));
    } else {
      navigate('/')
    }
  }, [dispatch, navigate, id, type]);

  // Handle API errors
  useEffect(() => {
    if (error) {
      Swal.fire({
        title: 'Error',
        text: error,
        icon: 'error',
        confirmButtonText: 'Okay'
      }).then(() => {
        dispatch(clearErrors());
      });
    }
  }, [error, dispatch]);

  // Format date helper function
  const formatDate = formatDisplayDate;

  // Format time helper function
  const formatTime = (timeString) => {
    if (!timeString) return 'Not administered';
    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours);
    const min = parseInt(minutes);
    
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const formattedMinutes = min.toString().padStart(2, '0');
    
    return `${hour12}:${formattedMinutes} ${ampm}`;
  };

  // Get dosage status badge
  const getDosageStatusBadge = (isDosageGiven) => {
    return isDosageGiven === 1 ? (
      <Badge bg="success">
        <i className="ri-check-line me-1"></i>
        Given
      </Badge>
    ) : (
      <Badge bg="warning">
        <i className="ri-close-line me-1"></i>
        Not Given
      </Badge>
    );
  };

  // Get page title
  const getPageTitle = () => {
    return type === 1 ? 'Medication Administration Logs' : 'Non-Medication Administration Logs';
  };

  // Get back link
  const getBackLink = () => {
    return type === 1 ? '/dashboard/app/medication/list' : '/dashboard/app/non-medication/list';
  };

  return (
    <>
      <LoaderModal showLoader={loading} />
      <div id='content-page' className='content-page'>
        <Container>
          <Row>
            <Col sm="12">
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <div className="header-title mb-2 mb-md-0">
                      <h4 className="card-title mb-1">
                        <i className={`${type === 1 ? 'ri-medicine-bottle-line' : 'ri-first-aid-kit-line'} me-2`}></i>
                        {getPageTitle()}
                      </h4>
                      {logs?.length > 0 && logs[0]?.medication && (
                        <h5 className="text-primary mb-2">
                          <i className="ri-capsule-line me-2"></i>
                          {logs[0].medication}
                        </h5>
                      )}
                      <p className="text-muted mb-0">
                        Administration logs filled by staff members
                      </p>
                    </div>
                    <div>
                      <Link 
                        to={getBackLink()} 
                        className="btn btn-outline-secondary btn-sm"
                        style={{"marginLeft": 30}}
                      >
                        <i className="ri-arrow-left-line me-1"></i>
                        Back to List
                      </Link>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  {logs?.length > 0 ? (
                    <>
                      <div className="table-responsive">
                        <Table striped hover className="mb-0">
                          <thead className="table-dark">
                            <tr>
                              <th scope="col">Date</th>
                              <th scope="col">{type === 1 ? 'Medication' : 'Non-Medication'}</th>
                              <th scope="col">Dosage Status</th>
                              <th scope="col">Time Given</th>
                              <th scope="col">Staff Signature</th>
                              <th scope="col">Comments</th>
                            </tr>
                          </thead>
                          <tbody>
                            {logs.map((log, index) => (
                              <tr key={log.id} className="align-middle">
                                <td>
                                  <div>
                                    <strong>{formatDate(log.medication_date)}</strong>
                                  </div>
                                </td>
                                <td>
                                  <span className="fw-semibold">{log.medication}</span>
                                </td>
                                <td>
                                  {getDosageStatusBadge(log.is_dosage_given)}
                                </td>
                                <td>
                                  <span className={log.time_given ? 'text-success fw-semibold' : 'text-muted'}>
                                    {formatTime(log.time_given)}
                                  </span>
                                </td>
                                <td>
                                  {log.staff_signiture ? (
                                    // staff_signiture is the uploaded signature image's S3 URL,
                                    // not display text - render it as an image like every other
                                    // signature field in the app (e.g. accident-reports.js),
                                    // instead of printing the raw path.
                                    <img
                                      src={log.staff_signiture}
                                      alt="Staff Signature"
                                      style={{ maxWidth: '120px', maxHeight: '50px', border: '1px solid #ddd', borderRadius: '4px', padding: '2px', background: '#fff' }}
                                    />
                                  ) : (
                                    <span className="text-muted">No signature</span>
                                  )}
                                </td>
                                <td>
                                  {log.comments ? (
                                    <div className="small">
                                      <i className="ri-chat-3-line me-1 text-info"></i>
                                      {log.comments}
                                    </div>
                                  ) : (
                                    <span className="text-muted">No comments</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      
                      {/* Summary Statistics */}
                      <Row className="mt-4">
                        <Col md={3}>
                          <Card className="bg-light border-0">
                            <Card.Body className="text-center py-3">
                              <i className="ri-file-list-2-line display-6 text-primary mb-2"></i>
                              <h6 className="mb-1">{logs.length}</h6>
                              <small className="text-muted">Total Entries</small>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="bg-light border-0">
                            <Card.Body className="text-center py-3">
                              <i className="ri-check-double-line display-6 text-success mb-2"></i>
                              <h6 className="mb-1">
                                {logs.filter(log => log.is_dosage_given === 1).length}
                              </h6>
                              <small className="text-muted">Given</small>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="bg-light border-0">
                            <Card.Body className="text-center py-3">
                              <i className="ri-close-circle-line display-6 text-warning mb-2"></i>
                              <h6 className="mb-1">
                                {logs.filter(log => log.is_dosage_given === 0).length}
                              </h6>
                              <small className="text-muted">Not Given</small>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="bg-light border-0">
                            <Card.Body className="text-center py-3">
                              <i className="ri-chat-3-line display-6 text-info mb-2"></i>
                              <h6 className="mb-1">
                                {logs.filter(log => log.comments && log.comments.trim()).length}
                              </h6>
                              <small className="text-muted">With Comments</small>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <NoDataFound />
                      <p className="text-muted mt-3">
                        No administration logs found. Logs will appear here once staff members 
                        start administering the {type === 1 ? 'medication' : 'non-medication'}.
                      </p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  )
}

export default MedicationLogs